import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Platform } from 'react-native';
import { 
  Text, 
  Card, 
  Title, 
  Paragraph, 
  Divider, 
  Button, 
  Snackbar, 
  Portal, 
  ActivityIndicator, 
  SegmentedButtons 
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { apiService } from '../services/api';
import { supabase } from '../services/supabase';
import { theme } from '../theme';
import TestApiSection from './ChatwootDashboard/TestApiSection';

// Helper para verificar se estamos em ambiente web
const isWeb = Platform.OS === 'web';

// Componentes para as diferentes seções do dashboard
// Importações temporárias para desenvolvimento - serão substituídas pelos componentes reais
const TitulosSection = ({ cliente }: { cliente: any }) => (
  <Card style={{ marginBottom: 16 }}>
    <Card.Content>
      <Title>Títulos do Cliente</Title>
      <Paragraph>Esta seção mostrará os títulos do cliente {cliente?.nome}</Paragraph>
    </Card.Content>
  </Card>
);

const CobrancasSection = ({ cliente }: { cliente: any }) => (
  <Card style={{ marginBottom: 16 }}>
    <Card.Content>
      <Title>Cobranças do Cliente</Title>
      <Paragraph>Esta seção mostrará as cobranças do cliente {cliente?.nome}</Paragraph>
    </Card.Content>
  </Card>
);

const PedidosSection = ({ cliente }: { cliente: any }) => (
  <Card style={{ marginBottom: 16 }}>
    <Card.Content>
      <Title>Pedidos do Cliente</Title>
      <Paragraph>Esta seção mostrará os pedidos do cliente {cliente?.nome}</Paragraph>
    </Card.Content>
  </Card>
);

const PontualidadeSection = ({ cliente }: { cliente: any }) => (
  <Card style={{ marginBottom: 16 }}>
    <Card.Content>
      <Title>Pontualidade do Cliente</Title>
      <Paragraph>Esta seção mostrará a pontualidade do cliente {cliente?.nome}</Paragraph>
    </Card.Content>
  </Card>
);

// TestApiSection é importado do arquivo separado

// Tipo para o contexto do Chatwoot
interface ChatwootContext {
  contact?: {
    id: number;
    name: string;
    email: string;
    phone_number: string;
    custom_attributes: any;
  };
  conversation?: {
    id: number;
    display_id: number;
    status: string;
    unread_count: number;
    labels: string[];
    additional_attributes: any;
  };
  meta?: {
    sender: {
      id: number;
      name: string;
      email: string;
    };
    assignee: {
      id: number;
      name: string;
      email: string;
    };
  };
}

// Função para validar se uma string é JSON válido
const isJSONValid = (str: string): boolean => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

const ChatwootDashboard = () => {
  // Estado para armazenar o contexto do Chatwoot
  const [chatwootContext, setChatwootContext] = useState<ChatwootContext | null>(null);
  
  // Estado para controlar a seção ativa do dashboard
  const [activeSection, setActiveSection] = useState('titulos');
  
  // Estado para o cliente atual
  const [clienteInfo, setClienteInfo] = useState<any>(null);
  
  // Estado para controlar carregamento
  const [loading, setLoading] = useState(false);
  
  // Estado para o snackbar
  const [snackbar, setSnackbar] = useState({
    visible: false,
    mensagem: '',
    tipo: 'info' as 'info' | 'success' | 'error'
  });

  // Efeito para configurar o listener de mensagens do Chatwoot
  useEffect(() => {
    // Só executa no ambiente web
    if (!isWeb) return;
    
    // Configurar o listener para receber mensagens do Chatwoot
    const handleMessage = (event: any) => {
      if (!isJSONValid(event.data)) return;
      
      try {
        const eventData = JSON.parse(event.data);
        console.log('Dados recebidos do Chatwoot:', eventData);
        
        // Atualizar o contexto do Chatwoot
        setChatwootContext(eventData);
        
        // Se tiver dados do contato, buscar informações do cliente
        if (eventData.contact) {
          buscarInformacoesCliente(eventData.contact);
        }
      } catch (error) {
        console.error('Erro ao processar mensagem do Chatwoot:', error);
      }
    };

    // Adicionar o listener com try/catch para evitar erros
    try {
      // @ts-ignore
      const windowObj = global.window || window;
      windowObj.addEventListener('message', handleMessage);
      
      // Solicitar informações do Chatwoot ao carregar
      if (windowObj.parent) {
        windowObj.parent.postMessage('chatwoot-dashboard-app:fetch-info', '*');
      }
    } catch (error) {
      console.error('Erro ao configurar listener:', error);
    }

    // Remover o listener quando o componente for desmontado
    return () => {
      try {
        // @ts-ignore
        const windowObj = global.window || window;
        windowObj.removeEventListener('message', handleMessage);
      } catch (error) {
        console.error('Erro ao remover listener:', error);
      }
    };
  }, []);

  // Função para buscar informações do cliente
  const buscarInformacoesCliente = async (contact: any) => {
    try {
      setLoading(true);
      
      // Verificar se há código do cliente nos atributos personalizados
      const codCliente = contact.custom_attributes?.codcli;
      
      // Se tiver código do cliente, usar diretamente
      if (codCliente) {
        console.log('Código do cliente encontrado nos atributos personalizados:', codCliente);
        setClienteInfo({
          codigo: codCliente,
          nome: contact.name || 'Cliente',
          telefone: contact.phone_number || '',
          email: contact.email || ''
        });
        
        setSnackbar({
          visible: true,
          mensagem: 'Cliente identificado pelo código!',
          tipo: 'success'
        });
        setLoading(false);
        return;
      }
      
      // Se não tiver código, buscar pelo telefone ou email
      const telefone = contact.phone_number?.replace(/\D/g, '');
      const email = contact.email;
      
      if (!telefone && !email) {
        setSnackbar({
          visible: true,
          mensagem: 'Não foi possível identificar o cliente. Telefone ou email não fornecidos.',
          tipo: 'error'
        });
        return;
      }
      
      // Obter a empresa do usuário logado
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSnackbar({
          visible: true,
          mensagem: 'Usuário não autenticado.',
          tipo: 'error'
        });
        return;
      }
      
      const { data: userData } = await supabase
        .from('usuarios')
        .select('empresa')
        .eq('id', user.id)
        .single();
      
      if (!userData?.empresa) {
        setSnackbar({
          visible: true,
          mensagem: 'Empresa do usuário não encontrada.',
          tipo: 'error'
        });
        return;
      }
      
      // Buscar cliente na API
      let clienteData;
      if (telefone) {
        // Buscar por telefone
        clienteData = await apiService.request(
          'API',
          userData.empresa,
          'FINANCEIROGETclienteportelefone',
          'post',
          { telefone }
        );
      } else if (email) {
        // Buscar por email
        clienteData = await apiService.request(
          'API',
          userData.empresa,
          'FINANCEIROGETclienteporemail',
          'post',
          { email }
        );
      }
      
      if (clienteData && clienteData.length > 0) {
        setClienteInfo(clienteData[0]);
        setSnackbar({
          visible: true,
          mensagem: 'Cliente encontrado com sucesso!',
          tipo: 'success'
        });
      } else {
        setSnackbar({
          visible: true,
          mensagem: 'Cliente não encontrado.',
          tipo: 'info'
        });
      }
    } catch (error) {
      console.error('Erro ao buscar informações do cliente:', error);
      setSnackbar({
        visible: true,
        mensagem: 'Erro ao buscar informações do cliente.',
        tipo: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para solicitar atualização do contexto do Chatwoot
  const atualizarContexto = () => {
    if (!isWeb) {
      setSnackbar({
        visible: true,
        mensagem: 'Função disponível apenas no ambiente web.',
        tipo: 'info'
      });
      return;
    }
    
    try {
      // @ts-ignore
      const windowObj = global.window || window;
      if (windowObj.parent) {
        windowObj.parent.postMessage('chatwoot-dashboard-app:fetch-info', '*');
        setSnackbar({
          visible: true,
          mensagem: 'Solicitando atualização de contexto...',
          tipo: 'info'
        });
      }
    } catch (error) {
      console.error('Erro ao solicitar atualização de contexto:', error);
      setSnackbar({
        visible: true,
        mensagem: 'Erro ao solicitar atualização de contexto.',
        tipo: 'error'
      });
    }
  };

  // Renderizar o conteúdo com base na seção ativa
  const renderSecaoAtiva = () => {
    if (!clienteInfo) {
      return (
        <Card style={styles.cardSemCliente}>
          <Card.Content>
            <Title>Nenhum cliente identificado</Title>
            <Paragraph>
              Não foi possível identificar o cliente com base nas informações fornecidas pelo Chatwoot.
              Verifique se o contato possui telefone ou email cadastrado.
            </Paragraph>
            <Button 
              mode="contained" 
              onPress={atualizarContexto}
              style={styles.button}
            >
              Atualizar Contexto
            </Button>
          </Card.Content>
        </Card>
      );
    }

    switch (activeSection) {
      case 'titulos':
        return <TitulosSection cliente={clienteInfo} />;
      case 'cobrancas':
        return <CobrancasSection cliente={clienteInfo} />;
      case 'teste':
        return <TestApiSection cliente={clienteInfo} />;
      case 'pedidos':
        return <PedidosSection cliente={clienteInfo} />;
      case 'pontualidade':
        return <PontualidadeSection cliente={clienteInfo} />;
      default:
        return <TitulosSection cliente={clienteInfo} />;
    }
  };

  return (
    <View style={styles.container}>
      {/* Cabeçalho com informações do cliente */}
      <Card style={styles.headerCard}>
        <Card.Content>
          <View style={styles.headerContent}>
            <View>
              <Title>
                {clienteInfo ? clienteInfo.nome : 'Cliente não identificado'}
              </Title>
              {clienteInfo && (
                <Paragraph>
                  {clienteInfo.documento || ''} • {clienteInfo.telefone || ''}
                </Paragraph>
              )}
            </View>
            <Button 
              mode="outlined" 
              onPress={atualizarContexto}
              icon="refresh"
            >
              Atualizar
            </Button>
          </View>
        </Card.Content>
      </Card>

      {/* Seletor de seções */}
      <SegmentedButtons
        value={activeSection}
        onValueChange={setActiveSection}
        style={styles.segmentedButtons}
        buttons={[
          {
            value: 'titulos',
            label: 'Títulos',
            icon: 'file-document-outline',
          },
          {
            value: 'cobrancas',
            label: 'Cobranças',
            icon: 'cash',
          },
          {
            value: 'pedidos',
            label: 'Pedidos',
            icon: 'shopping',
          },
          {
            value: 'pontualidade',
            label: 'Pontualidade',
            icon: 'chart-bar',
          },
          {
            value: 'teste',
            label: 'Teste API',
            icon: 'code-json',
          },
        ]}
      />

      {/* Conteúdo principal */}
      <ScrollView style={styles.content}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={styles.loadingText}>Carregando informações...</Text>
          </View>
        ) : (
          renderSecaoAtiva()
        )}
      </ScrollView>

      {/* Snackbar para feedback */}
      <Portal>
        <Snackbar
          visible={snackbar.visible}
          onDismiss={() => setSnackbar(prev => ({ ...prev, visible: false }))}
          duration={3000}
          style={{
            backgroundColor: 
              snackbar.tipo === 'success' ? '#4CAF50' : 
              snackbar.tipo === 'error' ? '#F44336' :
              '#2196F3'
          }}
        >
          {snackbar.mensagem}
        </Snackbar>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  headerCard: {
    margin: 16,
    elevation: 4,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  segmentedButtons: {
    marginHorizontal: 16,
    marginBottom: 16,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  cardSemCliente: {
    marginBottom: 16,
  },
  button: {
    marginTop: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
});

export default ChatwootDashboard;
